/**
 * MinimizedWindowsBar - PROJ-066
 * Shows minimized floating panels as a taskbar at the bottom of the screen
 * Similar to Windows/macOS dock for minimized windows
 */

import { Maximize2, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import type { WindowState } from '@/contexts/WindowManagerContext';

interface MinimizedWindowsBarProps {
  windows: WindowState[];
  onRestore: (windowId: string) => void;
  onClose: (windowId: string) => void;
}

export function MinimizedWindowsBar({
  windows,
  onRestore,
  onClose,
}: MinimizedWindowsBarProps) {
  if (windows.length === 0) return null;

  return (
    <div
      className={cn(
        'fixed bottom-4 left-1/2 -translate-x-1/2',
        'flex items-center gap-2 p-2',
        'bg-background/95 backdrop-blur-sm border rounded-lg shadow-lg',
        'z-[1050]' // Below floating panels but above content
      )}
    >
      <span className="text-xs text-muted-foreground px-2 border-r">
        Minimizados
      </span>

      {windows.map((window) => (
        <div
          key={window.id}
          className={cn(
            'flex items-center gap-1.5 px-3 py-1.5',
            'bg-muted/50 hover:bg-muted rounded-md',
            'transition-colors cursor-pointer group'
          )}
          onClick={() => onRestore(window.id)}
        >
          {/* Incident title */}
          <span className="text-sm max-w-[150px] truncate">
            {window.incident.incidentNumber || window.incident.title || 'Incidente'}
          </span>

          {/* Type badge */}
          {window.incident.type && (
            <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
              {window.incident.type.split('_')[0]}
            </Badge>
          )}

          {/* Action buttons (visible on hover) */}
          <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity ml-1">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-5 w-5"
                  onClick={(e) => {
                    e.stopPropagation();
                    onRestore(window.id);
                  }}
                >
                  <Maximize2 className="h-3 w-3" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Restaurar</TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-5 w-5 hover:bg-destructive hover:text-destructive-foreground"
                  onClick={(e) => {
                    e.stopPropagation();
                    onClose(window.id);
                  }}
                >
                  <X className="h-3 w-3" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Cerrar</TooltipContent>
            </Tooltip>
          </div>
        </div>
      ))}
    </div>
  );
}

export default MinimizedWindowsBar;
